/**
 * CAPDetailView - the workspace where a CAP gets acted on.
 *
 * Reads:
 *   useCap(capId)    - the CAP payload includes legal_actions for the
 *                      requesting user, so the action bar below is
 *                      ALWAYS in sync with the backend state machine.
 *   useLetter        - small letter summary to give the operator the
 *                      upstream context without a separate page load.
 *
 * Writes (via useTransitionCap):
 *   submit / approve / send_back / withdraw - each renders as a
 *   button when its action appears in legal_actions. send_back
 *   requires a comment (backend returns 422 without one); the UI
 *   demands it client-side too so the operator never sees that 422.
 *
 * Layout:
 *   Header        title, StatePill, PositionPill, back link
 *   Letter card   regulator_code + letter title (clickable to the
 *                 letter detail in a future P1.x)
 *   Entries list  action items with status chips
 *   Action bar    legal_action buttons + comment textarea
 *
 * Mounted at /framework/caps/:capId. Linked from QueueView rows.
 */
import { ThreadStrip } from "shared/thread/ThreadStrip";
import { RegulatorTag } from "shared/components/RegulatorTag";
import { formatDate } from "shared/time/formatDate";
import { useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { entityKindLabelFor } from "shared/vocab/entityKind";

import { useDateInputLang } from "shared/i18n/dateInputLang";

import { CapHistory, ChallengeBanner } from "framework/CapHistory";
import { offeredActions, verbKey } from "framework/capVerbs";
import { frameworkLabel, roleLabel } from "shared/vocab/workflowState";
import { capMovedMessage } from "framework/capMovedMessage";

import {
  useAddCapControl,
  useAddCapFinding,
  useAddEntryControl,
  useCap,
  useCreateEntry,
  useDraftCapActions,
  useCapControls,
  useCapFindings,
  useEntryControls,
  useLetter,
  useRemoveCapControl,
  useRemoveCapFinding,
  useRemoveEntryControl,
  useSuggestControls,
  useTransitionCap,
} from "framework/api/hooks";
import { useCatalogue, useFrameworks } from "framework/api/assessment_hooks";
import { PositionPill, StatePill } from "framework/components/StatePill";
import { MarsadIcon } from "shared/components/MarsadIcon";
import { EngineBusy } from "shared/components/EngineBusy";
import { GroundingNote } from "shared/components/GroundingNote";
import { EngineLanguageNote } from "shared/components/EngineLanguageNote";
import { useMe } from "shared/api/useMe";
import { useSeatHolders } from "shared/api/useSeatHolders";
import { useSearch } from "shared/search/useSearch";
import { refusalMessage } from "shared/api/refusalMessage";
import { useToast } from "shared/toast/ToastProvider";
import type {
  Action,
  CAPAvailableAction,
  CapControl,
  CapEntryControl,
  CapLinkedFinding,
  CAPState,
  ControlSuggestion,
  EngineGrounding,
  Position,
} from "shared/api/types";

import { Arrow } from "shared/components/Arrow";
import type { CAPEntryOut } from "shared/api/types";
import { usePatchCapEntry } from "framework/api/hooks";
import { Bdi } from "shared/components/Bdi";
import { Figure } from "shared/components/Figure";
// Visual emphasis per action - submit/approve are forward motions
// (emerald), send_back is destructive (red), withdraw is neutral.
const ACTION_STYLE: Record<Action, string> = {
  submit: "bg-brand-brass hover:bg-brand-brassDark text-brand-navy",
  approve: "bg-brand-brass hover:bg-brand-brassDark text-brand-navy",
  send_back: "bg-red-700 hover:bg-red-600 text-white",
  withdraw: "bg-slate-700 hover:bg-slate-600 text-white",
};

function ActionBar({
  capId,
  legalActions,
  availableActions,
  capState,
}: {
  capId: string;
  legalActions: Action[];
  availableActions: CAPAvailableAction[] | undefined;
  capState: string;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const seats = useSeatHolders();
  const [activeAction, setActiveAction] = useState<Action | null>(null);
  const [comment, setComment] = useState("");

  // One mutation per action; the buttons set activeAction so the
  // submit handler knows which one fired.
  const transition = useTransitionCap(capId);

  if (legalActions.length === 0) {
    return (
      <section className="mt-6 p-4 rounded border border-slate-200 bg-slate-50">
        <p className="text-sm text-slate-600">{t("caps.detail.actions.none")}</p>
      </section>
    );
  }

  function onClick(action: Action) {
    if (action === "send_back" && !comment.trim()) {
      setActiveAction(action); // ensure label shows; commentError below renders
      return;
    }
    setActiveAction(action);
    const moved = offeredActions(availableActions, capState).find(
      (a) => a.action === action,
    );
    transition.mutate(
      { action, comment: comment.trim() || null },
      {
        onSuccess: () => {
          setComment("");
          setActiveAction(null);
          // C3: say what happened AND what happens next. There was no
          // confirmation at all -- the buttons went stale and the
          // inbox kept listing the item until a reload.
          //
          // D1: keyed on the ACTION, not the state it landed in. A
          // preparer pressing Submit was told "Approved and sent on"
          // because the destination state is named for the reviewer's
          // act. The person who pressed the button knows what they
          // did; a confirmation that disagrees with them is worse
          // than none, because it makes them doubt what happened.
          // B3 -- an approved CAP KEEPS `to_position: approver`,
          // because that is who closed it, not who it now waits on.
          // Testing for the absence of a position therefore never
          // fired, and the one person who finished the CAP was told
          // it had been "sent on ... to the approver" -- to herself.
          // The terminal state is the whole signal.
          const closed = moved?.to_state === "approved";
          // F -- the BUTTON said "Pick up and edit" (C2, because from
          // sent_back a withdraw is recovery rather than retraction)
          // and the toast then said "Withdrawn". Two words for one act,
          // in the same second, is how a person ends up unsure what
          // they just did. The confirmation is chosen the same way the
          // verb was.
          const key =
            action === "approve" && closed
              ? "caps.detail.done.approveFinal"
              : action === "withdraw" && capState === "sent_back"
                ? "caps.detail.done.pickUp"
                : action === "submit" && capState === "sent_back"
                  ? "caps.detail.done.resubmit"
                  : action === "send_back" && capState === "approved"
                    ? "caps.detail.done.reopen"
                    : `caps.detail.done.${action}`;
          // Round 18 N11 -- the person it is with, where one person
          // holds that seat; the seat's own name where not. It read
          // "It is now with the approver." in the API's word.
          const to = moved?.to_position ?? "";
          toast.push({
            tone: "success",
            message: capMovedMessage(t, key, to, seats.data),
          });
        },
      },
    );
  }

  const showCommentError =
    activeAction === "send_back" && !comment.trim() && !transition.isPending;

  return (
    <section className="mt-6 p-4 rounded border border-slate-200 bg-white">
      <h2 className="text-sm font-semibold text-slate-700 mb-3">
        {t("caps.detail.actions.heading")}
      </h2>

      {/* D6 -- what approving commits the bank to, before the button
          rather than after it. Sixth round.
          Every other ceremony in the product says what its signature
          means -- the maturity assessment, the classification, the
          tolerance. This one offered Approve beside a comment box and
          left the consequence to be inferred, on the record that
          commits remediation dates to a regulator. */}
      {legalActions.includes("approve") && (
        <p
          data-testid="cap-approve-commits"
          className="mb-3 rounded border border-paper-2 bg-paper px-3 py-2 text-xs text-slate-700"
        >
          {/* Round 16 G14 -- the reviewer read the approver's
              commitment over "Approve and send to the approver": her
              approval forwards the plan; only the approver's accepts it
              on the bank's behalf. */}
          {(() => {
            const approve = offeredActions(availableActions, capState).find(
              (a) => a.action === "approve",
            );
            return approve && approve.to_state !== "approved"
              ? t("caps.detail.actions.approveForwardCommits", {
                  position: approve.to_position ?? "",
                })
              : t("caps.detail.actions.approveCommits");
          })()}
        </p>
      )}

      <label
        htmlFor="cap-action-comment"
        className="block text-xs font-medium text-slate-700"
      >
        {t("caps.detail.actions.commentLabel")}{" "}
        <span className="text-slate-400">
          {legalActions.includes("send_back")
            ? ""
            : t("caps.detail.actions.commentOptional")}
        </span>
      </label>
      <textarea
        dir="auto"
        id="cap-action-comment"
        rows={2}
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        className="mt-1 block w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
      />
      {showCommentError && (
        <p className="text-xs text-red-700 mt-1" role="alert">
          {t("caps.detail.actions.commentRequired")}
        </p>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        {/* C2: labelled from the server's destination, so the verb says
            what it does for the seat that holds it -- "approve" is
            forward for a reviewer and close for an approver, and
            "withdraw" on a sent-back CAP means pick it back up. */}
        {offeredActions(availableActions, capState).map((a) => (
          <button
            key={`${a.action}:${a.to_state}`}
            type="button"
            disabled={transition.isPending && activeAction === a.action}
            onClick={() => onClick(a.action)}
            data-testid={`cap-action-${a.action}`}
            className={[
              "px-3 py-1.5 rounded text-sm font-medium",
              ACTION_STYLE[a.action],
              "disabled:opacity-60 disabled:cursor-not-allowed",
            ].join(" ")}
          >
            {t(verbKey(a, capState), {
              position: roleLabel(a.to_position).toLowerCase(),
            })}
          </button>
        ))}
      </div>

      {transition.isError && (
        <p
          className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2"
          role="alert"
        >
          {refusalMessage(t, transition.error)}
        </p>
      )}
    </section>
  );
}

const STATUS_PILL: Record<string, string> = {
  compliant: "bg-emerald-100 text-emerald-800",
  partial: "bg-amber-100 text-amber-800",
  non_compliant: "bg-red-100 text-red-800",
  no_evidence: "bg-slate-100 text-slate-700",
};

/** P164: the autopilot's "draft the actions" affordance -- the Engine
 * drafts remediation action items grounded in the CAP's linked controls,
 * straight into the draft CAP. Position-gated; draft-only. */
function DraftActionsButton({
  capId,
  currentPosition,
  state,
  existingEntries,
  onRefused,
}: {
  /* Round 16 A22 -- the refusal is said on the plan, not only in a
     toast: the page stayed at "No action items yet." */
  onRefused: (sentence: string | null) => void;
  capId: string;
  currentPosition: string | null;
  state: string;
  /* Round 12 F5 -- "Draft actions with Engine" APPENDS rather than
     replaces, with nothing saying a draft already exists: one press
     took a three-item plan to eight items. Appending is the right
     behaviour -- a drafted action somebody has since edited must not be
     silently destroyed -- so what was missing is the sentence. */
  existingEntries: number;
}) {
  const { t } = useTranslation();
  const me = useMe();
  const toast = useToast();
  const draft = useDraftCapActions(capId);
  // C4 -- the Engine drafts FROM the linked controls, and the endpoint
  // 422s without any. The button was live regardless and refused by
  // toast, so the reader learned the prerequisite by being told no.
  const links = useCapControls(capId);
  const hasControl = (links.data?.length ?? 0) > 0;
  // Round 17 A31 -- "Link a control first" only on a settled, fresh
  // read: a stale empty list read six seconds after the raise said it
  // over a CAP whose control was already on the record.
  const linksSettled = links.isSuccess && !links.isFetching;
  const canEdit =
    me.data?.is_admin === true ||
    (currentPosition != null &&
      (me.data?.positions ?? []).some((p) => p === currentPosition));
  const [emptyDraft, setEmptyDraft] = useState(false);
  if (!canEdit || state !== "draft") return null;

  function onClick() {
    setEmptyDraft(false);
    onRefused(null);
    draft.mutate(undefined, {
      onSuccess: (res) => {
        // Round 14 A9 -- "Drafted 0 remediation actions" said nothing
        // about why. There are two answers, and only one is the Engine
        // having nothing to say.
        if (res.entries.length === 0) {
          setEmptyDraft(true);
          const dropped = res.grounding?.dropped ?? 0;
          toast.push({
            tone: "warning",
            message:
              dropped > 0
                ? t("caps.detail.draftActions.noneGrounded", { count: dropped })
                : t("caps.detail.draftActions.noneProposed"),
          });
          return;
        }
        toast.push({
          tone: "success",
          message: t("caps.detail.draftActions.success", {
            count: res.entries.length,
          }),
        });
      },
      onError: (e) => {
        const sentence =
          e.status === 503
            ? t("caps.detail.draftActions.engineOff")
            : refusalMessage(t, e) || t("caps.detail.draftActions.error");
        // Round 18 A36 -- a refusal is said once, in place beside the
        // button; it also toasted, so it was read twice.
        if (e.status !== 503) {
          onRefused(sentence);
          return;
        }
        toast.push({ tone: "warning", message: sentence });
      },
    });
  }

  return (
    <div data-testid="cap-draft-actions" className="flex flex-col items-end gap-1">
      <button
        type="button"
        data-testid="cap-draft-actions-btn"
        onClick={onClick}
        disabled={draft.isPending || !hasControl}
        title={hasControl ? undefined : t("caps.detail.draftActions.needsControl")}
        className="rounded border border-brand-brass/40 bg-brand-brass/5 px-2.5 py-1 text-xs font-medium text-brand-brassDark hover:bg-brand-brass/10 disabled:opacity-50"
      >
        {draft.isPending ? (
          <EngineBusy label={t("caps.detail.draftActions.drafting")} />
        ) : (
          t("caps.detail.draftActions.button")
        )}
      </button>
      {!hasControl && !linksSettled && (
        <p
          data-testid="cap-draft-actions-reading-controls"
          className="text-[11px] text-slate-500"
        >
          {t("caps.detail.draftActions.readingControls")}
        </p>
      )}
      {!hasControl && linksSettled && (
        <p
          data-testid="cap-draft-actions-needs-control"
          className="text-[11px] text-slate-500"
        >
          {t("caps.detail.draftActions.needsControl")}
        </p>
      )}
      {/* F5 -- said BEFORE the press, because after it the plan has
          already doubled and the reader is counting rows to work out
          what happened. */}
      {hasControl && existingEntries > 0 && (
        <p
          data-testid="cap-draft-actions-appends"
          className="max-w-xs text-end text-[11px] text-amber-800"
        >
          {t("caps.detail.draftActions.appends", { count: existingEntries })}
        </p>
      )}
      {/* ROUND 15 A16 -- an empty draft said what did not happen and
          offered nothing next. */}
      {emptyDraft && (
        <a
          href="#cap-remediated-controls"
          data-testid="cap-draft-actions-empty-next"
          className="text-[11px] text-brand-brassDark hover:underline"
        >
          {t("caps.detail.draftActions.noneProposedNext")}
        </a>
      )}
      {draft.data?.grounding && <GroundingNote grounding={draft.data.grounding} />}
      {draft.data?.grounding && <EngineLanguageNote />}
    </div>
  );
}

/** P264 -- manual action-item entry. Engine-off CAPs were dead shells
 * (entries only arrived via DraftActionsButton); a preparer / admin can
 * now add an entry by hand. Entries attach to an observation of the CAP's
 * letter (the gate the backend enforces), so we pull the letter to offer
 * the observation picker. Draft-state only, mirroring the edit gate. */
function NewEntryForm({
  capId,
  letterId,
  currentPosition,
  state,
}: {
  capId: string;
  letterId: string;
  currentPosition: string | null;
  state: string;
}) {
  const { t } = useTranslation();
  const dateInputLang = useDateInputLang();
  const me = useMe();
  const toast = useToast();
  const letter = useLetter(letterId);
  const createEntry = useCreateEntry(capId);
  const [observationId, setObservationId] = useState("");
  const [description, setDescription] = useState("");
  const [ownerRole, setOwnerRole] = useState("");
  const [dueDate, setDueDate] = useState("");

  const canEdit =
    me.data?.is_admin === true ||
    (currentPosition != null &&
      (me.data?.positions ?? []).some((p) => p === currentPosition));
  if (!canEdit || state !== "draft") return null;

  const observations = letter.data?.observations ?? [];
  const effectiveObs =
    observationId || (observations.length === 1 ? (observations[0]?.id ?? "") : "");
  const canSubmit =
    Boolean(effectiveObs) && description.trim().length > 0 && !createEntry.isPending;

  function onSubmit() {
    if (!effectiveObs || !description.trim()) return;
    createEntry.mutate(
      {
        observation_id: effectiveObs,
        description: description.trim(),
        owner_role: ownerRole.trim() || null,
        due_date: dueDate ? `${dueDate}T00:00:00Z` : null,
      },
      {
        onSuccess: () => {
          setDescription("");
          setOwnerRole("");
          setDueDate("");
          toast.push({ tone: "success", message: t("caps.detail.newEntry.added") });
        },
        onError: (e) => toast.push({ tone: "error", message: e.message }),
      },
    );
  }

  return (
    <details
      data-testid="cap-new-entry"
      className="mt-3 rounded border border-slate-200"
    >
      <summary className="cursor-pointer px-3 py-2 text-xs font-semibold text-brand-brassDark">
        + {t("caps.detail.newEntry.title")}
      </summary>
      <div className="space-y-2 border-t border-slate-100 p-3">
        {observations.length === 0 ? (
          <p className="text-xs text-rose-700">
            {t("caps.detail.newEntry.noObservations")}
          </p>
        ) : (
          <>
            {observations.length > 1 && (
              <label className="block text-xs">
                <span className="text-slate-600">
                  {t("caps.detail.newEntry.observation")}
                </span>
                <select
                  data-testid="cap-new-entry-observation"
                  value={effectiveObs}
                  onChange={(e) => setObservationId(e.target.value)}
                  className="mt-1 block w-full rounded border border-slate-300 px-2 py-1 text-xs"
                >
                  <option value="">—</option>
                  {observations.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.title}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label className="block text-xs">
              <span className="text-slate-600">
                {t("caps.detail.newEntry.description")}
              </span>
              <textarea
                dir="auto"
                data-testid="cap-new-entry-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                placeholder={t("caps.detail.newEntry.descriptionPlaceholder")}
                className="mt-1 block w-full rounded border border-slate-300 px-2 py-1 text-xs"
              />
            </label>
            <div className="flex flex-wrap gap-2">
              <label className="block text-xs">
                <span className="text-slate-600">
                  {t("caps.detail.newEntry.ownerRole")}
                </span>
                <input
                  dir="auto"
                  data-testid="cap-new-entry-owner"
                  value={ownerRole}
                  onChange={(e) => setOwnerRole(e.target.value)}
                  className="mt-1 block rounded border border-slate-300 px-2 py-1 text-xs"
                />
              </label>
              <label className="block text-xs">
                <span className="text-slate-600">
                  {t("caps.detail.newEntry.dueDate")}
                </span>
                <input
                  type="date"
                  lang={dateInputLang}
                  data-testid="cap-new-entry-due"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  className="mt-1 block rounded border border-slate-300 px-2 py-1 text-xs"
                />
              </label>
            </div>
            <button
              type="button"
              data-testid="cap-new-entry-submit"
              onClick={onSubmit}
              disabled={!canSubmit}
              className="rounded border border-brand-brassDark bg-brand-brass px-3 py-1 text-xs font-semibold text-brand-navy disabled:opacity-50"
            >
              {createEntry.isPending
                ? t("caps.detail.newEntry.adding")
                : t("caps.detail.newEntry.add")}
            </button>
          </>
        )}
      </div>
    </details>
  );
}

/** P108.2 -- the CAP leg. Lists the framework controls this CAP
 * remediates + their latest assessment status. D6: authoring is the
 * preparer's (admin too), whoever the CAP is currently sitting with --
 * a reviewer who may rewrite the plan is not reviewing it. */
function RemediatedControlsSection({ capId }: { capId: string }) {
  const { t } = useTranslation();
  const me = useMe();
  const isAdmin = me.data?.is_admin ?? false;
  // D6 -- authoring is the preparer's, whoever the CAP is sitting with.
  //
  // P115.4 opened this to the holder of the CAP's current position, so
  // the plan could be acted on by whoever had it. The consequence is
  // that a reviewer opening a CAP to CHALLENGE it was offered the verbs
  // that write it -- link a control, unlink one, ask the Engine for
  // more -- and an approver about to commit the bank to the plan was
  // offered them too. Reviewing something you may rewrite is not a
  // review. Sixth round for the complaint.
  //
  // The backend gate is deliberately left as it was: an administrator
  // supporting a tenant, or a preparer correcting a CAP that has moved
  // on, must still be able to. This is the affordance, and the
  // affordance follows whose job the work is.
  const canEdit = isAdmin || (me.data?.positions ?? []).some((p) => p === "preparer");
  const links = useCapControls(capId);
  const add = useAddCapControl(capId);
  const remove = useRemoveCapControl(capId);
  const suggest = useSuggestControls(capId);
  const toast = useToast();

  const [framework, setFramework] = useState<string>("");
  const [controlId, setControlId] = useState<string>("");
  const [suggestions, setSuggestions] = useState<ControlSuggestion[]>([]);
  const [grounding, setGrounding] = useState<EngineGrounding | null>(null);
  // D3 -- an empty result rendered NOTHING, so the button read as
  // broken. "Found none" and "did not run" look identical on screen
  // and are different answers; only one of them is the Engine's.
  const [suggestRan, setSuggestRan] = useState(false);
  // Round 14 A9 -- and "the Engine found none" was printed when the
  // Engine was never asked: an empty shortlist returns before any call.
  const [askedEngine, setAskedEngine] = useState(true);

  const onSuggest = () => {
    suggest.mutate(framework ? { framework } : {}, {
      onSuccess: (data) => {
        setSuggestions(data.suggestions);
        setGrounding(data.grounding);
        setAskedEngine(data.candidates_considered > 0);
        setSuggestRan(true);
      },
      onError: (e) =>
        toast.push({
          tone: "error",
          message:
            e.status === 503
              ? t("caps.detail.controls.engineOff")
              : (e.detail ?? "error"),
        }),
    });
  };

  const onLinkSuggestion = (s: ControlSuggestion) => {
    add.mutate(
      { framework_control_id: s.framework_control_id },
      {
        onSuccess: () => {
          setSuggestions((prev) =>
            prev.filter((x) => x.framework_control_id !== s.framework_control_id),
          );
          toast.push({ tone: "success", message: t("caps.detail.controls.added") });
        },
        onError: (e) => toast.push({ tone: "error", message: e.detail ?? "error" }),
      },
    );
  };

  const frameworks = useFrameworks();
  const catalogue = useCatalogue(framework || undefined);

  const pickerControls =
    catalogue.data?.domains.flatMap((d) => d.subdomains.flatMap((s) => s.controls)) ??
    [];

  const onAdd = () => {
    if (!controlId) return;
    add.mutate(
      { framework_control_id: controlId },
      {
        onSuccess: () => {
          toast.push({
            tone: "success",
            message: t("caps.detail.controls.added"),
          });
          setControlId("");
        },
        onError: (e) => toast.push({ tone: "error", message: e.detail ?? "error" }),
      },
    );
  };

  return (
    <section
      id="cap-remediated-controls"
      className="mt-5"
      data-testid="cap-controls-section"
    >
      <h2 className="text-sm font-semibold text-slate-700 mb-2">
        {t("caps.detail.controls.heading")}
      </h2>

      {links.data && links.data.length === 0 && (
        <p className="text-sm text-slate-500" data-testid="cap-controls-empty">
          {t("caps.detail.controls.empty")}
        </p>
      )}

      {links.data && links.data.length > 0 && (
        <ul className="space-y-2">
          {links.data.map((c: CapControl) => (
            <li
              key={c.link_id}
              data-testid="cap-control-row"
              className="p-3 rounded border border-slate-200 bg-white flex items-start gap-3"
            >
              <span
                className={[
                  "inline-flex items-center px-2 py-0.5 rounded text-xs font-medium",
                  c.assessment_status
                    ? (STATUS_PILL[c.assessment_status] ??
                      "bg-slate-100 text-slate-700")
                    : "bg-slate-100 text-slate-500",
                ].join(" ")}
              >
                {c.assessment_status
                  ? t(`caps.detail.controls.status.${c.assessment_status}`)
                  : t("caps.detail.controls.status.unassessed")}
              </span>
              <div className="flex-1">
                <p className="text-sm text-slate-900">
                  {/* Round 17 R5 -- the control opens its own drawer. */}
                  <Link
                    data-testid="cap-remediated-control-link"
                    to={`/framework/assessments?control=${encodeURIComponent(c.framework_control_id)}`}
                    className="font-mono text-xs text-brand-brassDark hover:underline me-1"
                  >
                    {frameworkLabel(c.framework_code)} {c.control_natural_id}
                  </Link>
                  {c.subdomain_name}
                </p>
                <p className="text-xs text-slate-500 mt-0.5">{c.control_text}</p>
              </div>
              {canEdit && (
                <button
                  type="button"
                  data-testid="cap-control-remove"
                  onClick={() =>
                    remove.mutate(c.framework_control_id, {
                      onError: (e) =>
                        toast.push({
                          tone: "error",
                          message: e.detail ?? "error",
                        }),
                    })
                  }
                  className="text-xs text-red-700 hover:underline"
                >
                  {t("caps.detail.controls.remove")}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {canEdit && (
        <div
          data-testid="cap-control-add"
          className="mt-3 flex flex-wrap items-end gap-2"
        >
          <label className="text-xs text-slate-600">
            <span className="block mb-1">{t("caps.detail.controls.framework")}</span>
            <select
              data-testid="cap-control-framework"
              value={framework}
              onChange={(e) => {
                setFramework(e.target.value);
                setControlId("");
              }}
              className="rounded border border-slate-300 px-2 py-1 text-sm"
            >
              <option value="">--</option>
              {(frameworks.data ?? []).map((f) => (
                <option key={f.code} value={f.code}>
                  {/* Round 17 I16 -- the framework's name, not its key. */}
                  {f.name || f.code}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs text-slate-600 flex-1 min-w-[12rem]">
            <span className="block mb-1">{t("caps.detail.controls.control")}</span>
            <select
              data-testid="cap-control-control"
              value={controlId}
              onChange={(e) => setControlId(e.target.value)}
              disabled={!framework}
              className="w-full rounded border border-slate-300 px-2 py-1 text-sm disabled:bg-slate-50"
            >
              <option value="">--</option>
              {pickerControls.map((ctrl) => (
                <option key={ctrl.id} value={ctrl.id}>
                  {ctrl.control_natural_id} -- {ctrl.control_text.slice(0, 60)}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            data-testid="cap-control-add-button"
            onClick={onAdd}
            disabled={!controlId || add.isPending}
            className="rounded bg-brand-brass px-3 py-1.5 text-sm font-medium text-brand-navy hover:bg-brand-brassDark disabled:opacity-50"
          >
            {t("caps.detail.controls.add")}
          </button>
          <button
            type="button"
            data-testid="cap-control-suggest-button"
            onClick={onSuggest}
            disabled={suggest.isPending}
            aria-busy={suggest.isPending}
            className="rounded border border-brand-brass/40 bg-brand-brass/5 px-3 py-1.5 text-sm font-medium text-brand-brassDark hover:bg-brand-brass/10 disabled:opacity-50"
          >
            {suggest.isPending ? (
              <EngineBusy label={t("caps.detail.controls.suggesting")} />
            ) : (
              t("caps.detail.controls.suggest")
            )}
          </button>
        </div>
      )}

      {canEdit && suggestRan && suggestions.length === 0 && (
        <p
          data-testid="cap-control-suggestions-empty"
          className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600"
        >
          {askedEngine
            ? t("caps.detail.controls.suggestionsNone")
            : t("caps.detail.controls.suggestionsNotAsked")}
        </p>
      )}

      {canEdit && suggestions.length > 0 && (
        <div
          data-testid="cap-control-suggestions"
          className="mt-3 rounded-lg border border-indigo-200 bg-indigo-50/40 p-3"
        >
          <p className="mb-2 text-xs font-medium text-slate-700">
            {t("caps.detail.controls.suggestionsHeading")}
          </p>
          <ul className="space-y-2">
            {suggestions.map((s) => (
              <li
                key={s.framework_control_id}
                data-testid={`cap-control-suggestion-${s.control_natural_id}`}
                className="flex items-start gap-3"
              >
                <span className="text-[10px] tabular-nums text-indigo-700 mt-0.5">
                  {Math.round(s.confidence * 100)}%
                </span>
                <div className="flex-1">
                  <p className="text-sm text-slate-900">
                    <span className="font-mono text-xs text-slate-500 me-1">
                      {frameworkLabel(s.framework_code)} {s.control_natural_id}
                    </span>
                  </p>
                  <p className="text-xs text-slate-500">{s.rationale}</p>
                </div>
                <button
                  type="button"
                  data-testid={`cap-control-suggestion-link-${s.control_natural_id}`}
                  onClick={() => onLinkSuggestion(s)}
                  className="text-xs text-brand-brassDark hover:underline"
                >
                  {t("caps.detail.controls.link")}
                </button>
              </li>
            ))}
          </ul>
          <GroundingNote grounding={grounding} />
          <EngineLanguageNote />
        </div>
      )}
    </section>
  );
}

const SEVERITY_PILL: Record<string, string> = {
  critical: "bg-red-100 text-red-900",
  high: "bg-amber-100 text-amber-900",
  medium: "bg-slate-100 text-slate-700",
  low: "bg-slate-50 text-slate-600",
};

// P76: the telemetry findings this CAP remediates. The CAP owner (or an
// admin) links them via a search-backed picker; the FindingDetail page
// shows the symmetric back-link.
function LinkedFindingsSection({
  capId,
  currentPosition,
}: {
  capId: string;
  currentPosition: string | null;
}) {
  const { t } = useTranslation();
  const me = useMe();
  const isAdmin = me.data?.is_admin ?? false;
  const canEdit =
    isAdmin ||
    (currentPosition != null &&
      (me.data?.positions ?? []).some((p) => p === currentPosition));
  const links = useCapFindings(capId);
  const add = useAddCapFinding(capId);
  const remove = useRemoveCapFinding(capId);
  const toast = useToast();

  const [query, setQuery] = useState("");
  const search = useSearch({ q: query, kinds: "finding", limit: 8 });
  const findingRows: CapLinkedFinding[] = Array.isArray(links.data) ? links.data : [];
  const linkedIds = new Set(findingRows.map((l) => l.finding_id));
  const hits = (search.data?.results ?? []).filter(
    (h) => h.kind === "finding" && !linkedIds.has(h.id),
  );

  const onAdd = (findingId: string) => {
    add.mutate(
      { finding_id: findingId },
      {
        onSuccess: () => {
          toast.push({ tone: "success", message: t("caps.detail.findings.added") });
          setQuery("");
        },
        onError: (e) => toast.push({ tone: "error", message: e.detail ?? "error" }),
      },
    );
  };

  return (
    <section className="mt-5" data-testid="cap-findings-section">
      <h2 className="text-sm font-semibold text-slate-700 mb-2">
        {t("caps.detail.findings.heading")}
      </h2>

      {links.isSuccess && findingRows.length === 0 && (
        <p className="text-sm text-slate-500" data-testid="cap-findings-empty">
          {t("caps.detail.findings.empty")}
        </p>
      )}

      {findingRows.length > 0 && (
        <ul className="space-y-2">
          {findingRows.map((f: CapLinkedFinding) => (
            <li
              key={f.link_id}
              data-testid="cap-finding-row"
              data-finding-id={f.finding_id}
              className="p-3 rounded border border-slate-200 bg-white flex items-start gap-3"
            >
              <span
                className={[
                  "inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold uppercase",
                  SEVERITY_PILL[f.severity] ?? "bg-slate-100 text-slate-700",
                ].join(" ")}
              >
                {f.severity}
              </span>
              <div className="flex-1">
                <Link
                  to={`/twin/findings/${f.finding_id}`}
                  className="text-sm text-slate-900 hover:text-brand-brass"
                >
                  {f.title}
                </Link>
                <p className="text-xs text-slate-500 mt-0.5">
                  {/* Round 17 L9 -- the kind in words, a function as one. */}
                  {entityKindLabelFor(f.entity_kind, f.entity_service_class)}{" "}
                  {t("screens.cAPDetailView.middot")} {f.status}
                </p>
              </div>
              {canEdit && (
                <button
                  type="button"
                  data-testid="cap-finding-remove"
                  onClick={() =>
                    remove.mutate(f.finding_id, {
                      onError: (e) =>
                        toast.push({ tone: "error", message: e.detail ?? "error" }),
                    })
                  }
                  className="text-xs text-red-700 hover:underline"
                >
                  {t("caps.detail.findings.remove")}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {canEdit && (
        <div data-testid="cap-finding-add" className="mt-3 space-y-2">
          <input
            dir="auto"
            type="text"
            data-testid="cap-finding-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("caps.detail.findings.searchPlaceholder")}
            className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
          />
          {/* A failed search and an empty one used to render the same
              nothing, so a 403 read as "your query is wrong". Both are
              now said out loud. */}
          {query.trim().length > 0 && search.isError && (
            <p data-testid="cap-finding-search-error" className="text-xs text-rose-700">
              {t("caps.detail.findings.searchFailed")}
            </p>
          )}
          {query.trim().length > 0 &&
            search.isSuccess &&
            hits.length === 0 &&
            !add.isPending && (
              <p
                data-testid="cap-finding-search-empty"
                className="text-xs text-slate-500"
              >
                {t("caps.detail.findings.searchNoHits")}
              </p>
            )}
          {query.trim().length > 0 && hits.length > 0 && (
            <ul className="rounded border border-slate-200 bg-white divide-y divide-slate-100">
              {hits.map((h) => (
                <li key={h.id}>
                  <button
                    type="button"
                    data-testid="cap-finding-add-hit"
                    data-finding-id={h.id}
                    onClick={() => onAdd(h.id)}
                    disabled={add.isPending}
                    className="w-full text-start px-2 py-1.5 text-sm text-slate-800 hover:bg-brand-brass/10 disabled:opacity-50"
                  >
                    + {h.display_name}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}

// P115.7: entry-grained control chips. Surfaces the cap_entry_control_links
// junction (P115.6) -- which framework control each action item remediates.
// Compact: chips + an inline picker that's collapsed until an editor opens it.
function EntryControlsSection({ capId, entryId }: { capId: string; entryId: string }) {
  const { t } = useTranslation();
  const me = useMe();
  const isAdmin = me.data?.is_admin ?? false;
  // D5 (round 8) -- the same rule the CAP-level section already
  // follows, and the reason is the same: reviewing something you may
  // rewrite is not a review.
  //
  // That section was fixed and this one was not, so "Unlink" and
  // "Suggest with Engine" correctly vanished for the reviewer and the
  // approver while "+ Link control" stayed -- once per unlinked action
  // item, on the CAP they are there to challenge. The backend gate is
  // deliberately left as it was: an administrator supporting a tenant,
  // or a preparer correcting a CAP that has moved on, must still be
  // able to. This is the affordance, and the affordance follows whose
  // job the work is.
  const canEdit = isAdmin || (me.data?.positions ?? []).some((p) => p === "preparer");
  const links = useEntryControls(capId, entryId);
  const add = useAddEntryControl(capId, entryId);
  const remove = useRemoveEntryControl(capId, entryId);
  const toast = useToast();

  const [adding, setAdding] = useState(false);
  const [framework, setFramework] = useState("");
  const [controlId, setControlId] = useState("");
  const frameworks = useFrameworks();
  const catalogue = useCatalogue(adding && framework ? framework : undefined);
  const pickerControls =
    catalogue.data?.domains.flatMap((d) => d.subdomains.flatMap((s) => s.controls)) ??
    [];

  const rows: CapEntryControl[] = Array.isArray(links.data) ? links.data : [];

  const onAdd = () => {
    if (!controlId) return;
    add.mutate(
      { framework_control_id: controlId },
      {
        onSuccess: () => {
          toast.push({
            tone: "success",
            message: t("caps.detail.entryControls.added"),
          });
          setControlId("");
          setFramework("");
          setAdding(false);
        },
        onError: (e) => toast.push({ tone: "error", message: e.detail ?? "error" }),
      },
    );
  };

  // Keep entry rows clean when there's nothing to show + nothing to do.
  if (rows.length === 0 && !canEdit) return null;

  return (
    <div className="mt-2" data-testid="entry-controls" data-entry-id={entryId}>
      <div className="flex flex-wrap items-center gap-1.5">
        {rows.map((c) => (
          <span
            key={c.link_id}
            data-testid="entry-control-chip"
            data-control-id={c.framework_control_id}
            title={c.control_text}
            className="inline-flex items-center gap-1 rounded-full bg-paper-2 px-2 py-0.5 text-[11px] text-slate-700 ring-1 ring-slate-200"
          >
            <span className="font-mono">
              {frameworkLabel(c.framework_code)} {c.control_natural_id}
            </span>
            {canEdit && (
              <button
                type="button"
                data-testid="entry-control-remove"
                aria-label="Remove control link"
                onClick={() =>
                  remove.mutate(c.framework_control_id, {
                    onError: (e) =>
                      toast.push({ tone: "error", message: e.detail ?? "error" }),
                  })
                }
                className="text-slate-400 hover:text-rose-700"
              >
                {t("screens.cAPDetailView.times")}
              </button>
            )}
          </span>
        ))}
        {rows.length === 0 && (
          <span className="text-[11px] text-slate-400">
            {t("caps.detail.entryControls.empty")}
          </span>
        )}
        {canEdit && !adding && (
          <button
            type="button"
            data-testid="entry-control-add-toggle"
            onClick={() => setAdding(true)}
            className="text-[11px] font-medium text-brand-brassDark hover:underline"
          >
            + {t("caps.detail.entryControls.add")}
          </button>
        )}
      </div>
      {canEdit && adding && (
        <div
          data-testid="entry-control-picker"
          className="mt-1.5 flex flex-wrap items-end gap-2"
        >
          <select
            data-testid="entry-control-framework"
            value={framework}
            onChange={(e) => {
              setFramework(e.target.value);
              setControlId("");
            }}
            className="rounded border border-slate-300 px-2 py-1 text-xs"
          >
            <option value="">--</option>
            {(frameworks.data ?? []).map((f) => (
              <option key={f.code} value={f.code}>
                {f.name || f.code}
              </option>
            ))}
          </select>
          <select
            data-testid="entry-control-control"
            value={controlId}
            onChange={(e) => setControlId(e.target.value)}
            disabled={!framework}
            className="min-w-[10rem] rounded border border-slate-300 px-2 py-1 text-xs disabled:bg-slate-50"
          >
            <option value="">--</option>
            {pickerControls.map((ctrl) => (
              <option key={ctrl.id} value={ctrl.id}>
                {ctrl.control_natural_id}
              </option>
            ))}
          </select>
          <button
            type="button"
            data-testid="entry-control-add-button"
            onClick={onAdd}
            disabled={!controlId || add.isPending}
            className="rounded bg-brand-brass px-2 py-1 text-xs font-medium text-brand-navy hover:bg-brand-brassDark disabled:opacity-50"
          >
            {t("caps.detail.entryControls.link")}
          </button>
          <button
            type="button"
            onClick={() => {
              setAdding(false);
              setFramework("");
              setControlId("");
            }}
            className="text-xs text-slate-500 hover:text-brand-brassDark"
          >
            {t("caps.detail.entryControls.cancel")}
          </button>
        </div>
      )}
    </div>
  );
}

/** D11 -- an action item's owner and date, and a way to set them.
 *
 * Draft only, and the preparer's, mirroring the gate: once a plan is in
 * review the plan is what is being reviewed, and editing it underneath
 * the reviewer is the defect D6 removed from the control links.
 */
function EntryOwnerLine({
  capId,
  entry,
  editable,
  pickUpFirst = false,
}: {
  capId: string;
  entry: CAPEntryOut;
  editable: boolean;
  /** Round 16 H26 -- a sent-back plan is read-only until its preparer
   *  picks it up, and the card said nothing she could act on. */
  pickUpFirst?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const dateInputLang = useDateInputLang();
  const toast = useToast();
  const patch = usePatchCapEntry(capId, entry.id);
  const [open, setOpen] = useState(false);
  const [owner, setOwner] = useState(entry.owner_role ?? "");
  const [due, setDue] = useState(entry.due_date ? entry.due_date.slice(0, 10) : "");

  const known = entry.owner_role || entry.due_date;

  if (!open) {
    return (
      <p className="mt-1 text-xs text-slate-500">
        {known ? (
          <>
            {entry.owner_role && <Bdi>{entry.owner_role}</Bdi>}
            {entry.owner_role && entry.due_date && " · "}
            {entry.due_date && (
              <Figure>{formatDate(entry.due_date, i18n.language)}</Figure>
            )}
          </>
        ) : (
          <span data-testid={`entry-unowned-${entry.id}`} className="italic">
            {t("caps.detail.entryUnowned")}
            {/* Round 12 D5, fifth round -- the prompt deliberately and
                correctly says "Do NOT assign an owner. Accountability is
                the bank's to decide", and the screen never said it, so
                five blank owners read as five omissions. */}{" "}
            <span data-testid={`entry-unowned-why-${entry.id}`} className="not-italic">
              {t("caps.detail.entryUnownedWhy")}
            </span>
          </span>
        )}
        {/* Round 17 A32 -- a space in the TEXT, ninth round: `ms-2` parts
            the words on screen and not in innerText, so "name one.Set an
            owner" read glued to a copy and to a screen reader. */}
        {(editable || (pickUpFirst && !known)) && " "}
        {editable && (
          <button
            type="button"
            data-testid={`entry-edit-${entry.id}`}
            onClick={() => setOpen(true)}
            className="ms-2 font-medium text-brand-brassDark hover:underline"
          >
            {known ? t("common.edit") : t("caps.detail.entrySetOwner")}
          </button>
        )}
        {!editable && pickUpFirst && !known && (
          <span
            data-testid={`entry-pick-up-${entry.id}`}
            className="ms-2 font-medium text-slate-700"
          >
            {t("caps.detail.entrySetOwnerAfterPickUp")}
          </span>
        )}
      </p>
    );
  }

  return (
    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
      <input
        dir="auto"
        data-testid={`entry-owner-${entry.id}`}
        value={owner}
        onChange={(e) => setOwner(e.target.value)}
        placeholder={t("caps.detail.entryOwnerPlaceholder")}
        className="rounded border border-slate-300 px-2 py-1"
      />
      <input
        type="date"
        lang={dateInputLang}
        data-testid={`entry-due-${entry.id}`}
        value={due}
        onChange={(e) => setDue(e.target.value)}
        className="rounded border border-slate-300 px-2 py-1"
      />
      <button
        type="button"
        data-testid={`entry-save-${entry.id}`}
        disabled={patch.isPending}
        onClick={() =>
          patch.mutate(
            {
              owner_role: owner.trim() || null,
              due_date: due ? `${due}T00:00:00Z` : null,
            },
            {
              onSuccess: () => {
                setOpen(false);
                toast.push({
                  tone: "success",
                  message: t("caps.detail.entrySaved"),
                });
              },
            },
          )
        }
        className="rounded bg-brand-brass px-2 py-1 font-medium text-brand-navy disabled:opacity-50"
      >
        {t("common.save")}
      </button>
      <button
        type="button"
        onClick={() => setOpen(false)}
        className="text-slate-500 hover:underline"
      >
        {t("common.cancel")}
      </button>
    </div>
  );
}

export function CAPDetailView() {
  const { t } = useTranslation();
  const { capId } = useParams<{ capId: string }>();
  const me = useMe();
  // D11 -- authoring the plan is the preparer's, the same rule D6 drew
  // for the control links.
  const canWriteEntries =
    (me.data?.is_admin ?? false) ||
    (me.data?.positions ?? []).some((p) => p === "preparer");
  const cap = useCap(capId);
  const letter = useLetter(cap.data?.letter_id);
  /* ROUND 13 F7 -- the grounding verdict for actions drafted on the
     way here.
     A CAP raised from a control drawer arrives carrying drafted
     actions and printed no grounding line at all, while the CAP's own
     drafting verb prints one with the nominal-attribution warning.
     Round 12 recorded the same asymmetry. The verdict travels with
     the navigation because it belongs to a call that finished before
     this page existed. */
  const arrivedWith = (useLocation().state ?? {}) as {
    draftGrounding?: EngineGrounding;
    /* Round 16 A22 -- raised with the Engine's drafting refused. */
    draftRefused?: boolean;
  };
  const [draftRefusal, setDraftRefusal] = useState<string | null>(
    arrivedWith.draftRefused ? t("caps.detail.draftActions.createdRefused") : null,
  );

  if (cap.isLoading) {
    return (
      <div className="text-sm text-slate-500">
        {t("screens.cAPDetailView.loadingCap")}
      </div>
    );
  }
  if (cap.isError) {
    return (
      <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">
        {cap.error.detail}
      </p>
    );
  }
  if (!cap.data) {
    return null;
  }

  const c = cap.data;
  const stateLabel = t(`caps.detail.state_labels.${c.state}`);
  const positionLabel = t(`queue.${c.current_position}`);

  // D6 -- back to the reader's OWN queue, not the CAP's.
  //
  // This pointed at `current_position`, which is where the CAP is, not
  // where the reader came from. So a reviewer who had just passed a CAP
  // to the approver was offered "Back to the approver queue" -- a queue
  // that is not his, holding work he cannot act on. Sixth round.
  //
  // His own seat when he holds one, and My Work when he holds several
  // or none, which is the one inbox that is always his.
  const myPositions = me.data?.positions ?? [];
  const backTo = myPositions.includes(c.current_position)
    ? `/framework/queue/${c.current_position}`
    : myPositions.length === 1
      ? `/framework/queue/${myPositions[0]}`
      : "/my-work";
  const backLabel = myPositions.includes(c.current_position)
    ? t("caps.detail.backToQueue", {
        position: t(`queue.${c.current_position}`),
      })
    : myPositions.length === 1
      ? t("caps.detail.backToQueue", { position: t(`queue.${myPositions[0]}`) })
      : t("caps.detail.backToMyWork");

  return (
    <article className="max-w-5xl">
      <Link
        to={backTo}
        data-testid="cap-back-link"
        className="text-sm text-slate-500 hover:text-brand-brassDark"
      >
        <Arrow kind="back" /> {backLabel}
      </Link>

      {arrivedWith.draftGrounding && (
        <div data-testid="cap-arrival-grounding" className="mt-3">
          <GroundingNote grounding={arrivedWith.draftGrounding} />
          <EngineLanguageNote />
        </div>
      )}

      <header className="mt-3 flex items-center gap-3 flex-wrap">
        <h1 className="text-2xl font-semibold">
          <MarsadIcon
            name="cap-remedy"
            size={20}
            className="me-2 inline-block align-[-0.1em] text-brand-brassDark"
          />
          {c.title}
        </h1>
        <StatePill state={c.state as CAPState} label={stateLabel} />
        <PositionPill position={c.current_position as Position} label={positionLabel} />
      </header>

      {/* C1: the challenge opens the page. The approver signs after it
          and never saw it; the preparer was shown "Sent back" and a
          button. */}
      <div className="mt-4">
        <ChallengeBanner capId={c.id} capState={c.state} />
      </div>

      <section className="mt-4 p-3 rounded border border-slate-200 bg-slate-50 text-sm">
        {letter.data?.raised_from_control ? (
          <Link
            data-testid="cap-raised-from-control"
            to={`/framework/letters/${cap.data?.letter_id}`}
            className="text-brand-brassDark hover:underline"
          >
            {t("caps.detail.raisedFromControl", {
              control: letter.data.raised_from_control,
            })}
          </Link>
        ) : (
          <span className="text-slate-500">{t("caps.detail.letter")}: </span>
        )}
        {letter.data?.raised_from_control ? null : letter.data ? (
          <Link
            data-testid="cap-letter-link"
            to={`/framework/letters/${cap.data?.letter_id}`}
            className="text-brand-brassDark hover:underline"
          >
            <RegulatorTag code={letter.data.regulator_code} />
            {letter.data.title}
          </Link>
        ) : (
          <span className="text-slate-400">...</span>
        )}
      </section>
      {/* Round 17 R5 -- the plan carries its letter's thread, so the
          reader sees where it sits between the ask and the proof. */}
      {cap.data?.letter_id && (
        <ThreadStrip subject="letter" id={cap.data.letter_id} capId={cap.data.id} />
      )}

      <section className="mt-5">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-sm font-semibold text-slate-700">
            {t("caps.detail.entries")}
          </h2>
          <DraftActionsButton
            capId={c.id}
            currentPosition={c.current_position}
            state={c.state}
            existingEntries={c.entries.length}
            onRefused={setDraftRefusal}
          />
        </div>
        {draftRefusal && (
          <p
            data-testid="cap-draft-refused"
            role="status"
            className="mb-2 rounded border border-amber-300 bg-amber-50 p-2 text-sm text-amber-900"
          >
            {draftRefusal}
          </p>
        )}
        {c.entries.length === 0 && draftRefusal ? null : c.entries.length === 0 ? (
          <p className="text-sm text-slate-500">{t("caps.detail.noEntries")}</p>
        ) : (
          <ul className="space-y-2">
            {c.entries.map((entry) => (
              <li
                key={entry.id}
                className="p-3 rounded border border-slate-200 bg-white flex items-start gap-3"
              >
                <span
                  className={[
                    "inline-flex items-center px-2 py-0.5 rounded text-xs font-medium",
                    entry.status === "complete"
                      ? "bg-emerald-100 text-emerald-800"
                      : entry.status === "in_progress"
                        ? "bg-amber-100 text-amber-800"
                        : "bg-slate-100 text-slate-700",
                  ].join(" ")}
                >
                  {t(`caps.detail.entryStatus.${entry.status}`)}
                </span>
                <div className="flex-1">
                  <p className="text-sm text-slate-900">{entry.description}</p>
                  {/* D11 -- an owner and a date, and a way to set them.
                      The Engine leaves the owner blank on purpose -- it
                      supplied "ciso" from the prompt's own example, and
                      a record naming an owner nobody chose is worse
                      than an empty field. But nothing let a person fill
                      it, so a reviewer's challenge asking who owns this
                      could not be answered by anybody. Third round. */}
                  <EntryOwnerLine
                    capId={c.id}
                    entry={entry}
                    editable={canWriteEntries && c.state === "draft"}
                    pickUpFirst={canWriteEntries && c.state === "sent_back"}
                  />
                  <EntryControlsSection capId={c.id} entryId={entry.id} />
                </div>
              </li>
            ))}
          </ul>
        )}
        <NewEntryForm
          capId={c.id}
          letterId={c.letter_id}
          currentPosition={c.current_position}
          state={c.state}
        />
      </section>

      <RemediatedControlsSection capId={c.id} />

      <LinkedFindingsSection capId={c.id} currentPosition={c.current_position} />

      <CapHistory capId={c.id} />

      <ActionBar
        capId={c.id}
        legalActions={c.legal_actions}
        availableActions={c.available_actions}
        capState={c.state}
      />
    </article>
  );
}
